import React, { FunctionComponent } from 'react';
import styles from './AddToCalendarButton.scss';
import { addToCalendarHandler, CalendarType } from './helpers/addToCalendar';
import appleCalendarIcon from './images/apple-calendar-icon.svg';
import googleCalendarIcon from './images/google-calendar-icon.svg';
import outlookCalendarIcon from './images/outlook-calendar-icon.svg';
import outlookWebCalendarIcon from './images/outlook-web-calendar-icon.svg';
import yahooCalendarIcon from './images/yahoo-calendar-icon.svg';
import generateHash from '../../utils/generateHash';
import parseCurrency from '../../utils/parseCurrency';
import Button from '../Button/Button';

import queryString from 'query-string';
import { useTranslation } from 'react-i18next';

// OTG 2023 - Tom Hedges

type AddToCalendarButtonProps = {
  buttonText?: string;
  ownerName: string;
  currencyCode: string;
  targetAmount: number;
  charityName: string;
  eventName: string;
  calendarType: CalendarType;
  url: string;
  startTime: Date;
  endTime?: Date;
  classNames?: string;
};

export const AddToCalendarButton: FunctionComponent<AddToCalendarButtonProps> = ({
  ownerName,
  targetAmount,
  currencyCode,
  charityName,
  eventName,
  calendarType,
  startTime,
  endTime,
  buttonText,
  classNames,
  url,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const handleButtonClick = () => {
    const targetAmountFormatted = parseCurrency(currencyCode, targetAmount, {
      roundDown: true,
      wholeNumber: true,
    });
    const shareHash = generateHash();
    const urlWithQueryString = `${url}?${queryString.stringify({
      utm_source: `${calendarType.toLowerCase()}-reminder`,
      utm_term: shareHash,
    })}`;

    const doubleLineSpacing =
      calendarType === CalendarType.Apple ||
      calendarType === CalendarType.Outlook
        ? '\\n\\n'
        : '\n\n';

    const event = {
      startTime,
      endTime: endTime || startTime,
      location: t('common.justgiving'),
      title: t('donationReminder.calendar.title', { ownerName, charityName }),
      description: `${t('donationReminder.calendar.descriptionLine1', {
        ownerName,
        eventName,
        targetAmountFormatted,
        charityName,
      })}${doubleLineSpacing}${t('donationReminder.calendar.descriptionLine2', {
        urlWithQueryString,
      })}`,
    };

    addToCalendarHandler(event, calendarType);
  };

  const getCalendarIcon = () => {
    switch (calendarType) {
      case CalendarType.Apple:
        return appleCalendarIcon;
      case CalendarType.Google:
        return googleCalendarIcon;
      case CalendarType.Outlook:
        return outlookCalendarIcon;
      case CalendarType.OutlookWeb:
        return outlookWebCalendarIcon;
      case CalendarType.Yahoo:
        return yahooCalendarIcon;
      default:
        return '';
    }
  };

  const label = buttonText || calendarType;

  return (
    <Button
      type="button"
      onClick={handleButtonClick}
      className={`${styles.reminder} ${classNames || ''}`}
      outlined
      data-qa={`add-calendar-button-${calendarType.toLowerCase()}`}
    >
      <img src={getCalendarIcon()} alt="" />
      {label}
    </Button>
  );
};
